import { inject, Injectable } from '@angular/core';
import { logger, ChangeTyped, IndexedService, OUTLINES_STORE, SearchResult } from '@damofire/data-commons';
import { LockingIndexedService } from '../indexed/locking-indexed.service';
import { WarehouseOutlineSegment } from '../warehouse/warehouse.dto';
import { generateAndAssignOutlineIds, WarehouseOutlineService } from './warehouse-outline.service';
import { AuthDtoSignerService } from '@damofire/data-security';
import { ServiceEventType } from '@damofire/data-angular-commons';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';

@Injectable()
export class IdbWarehouseOutlineService extends WarehouseOutlineService {
  private readonly dtoSigner: AuthDtoSignerService = inject(AuthDtoSignerService);
  private readonly warehouseSyncEventService: WarehouseSyncEventService = inject(WarehouseSyncEventService);

  constructor(
    private readonly idbService: IndexedService,
    private readonly lockingIdbService: LockingIndexedService,
  ) {
    super();
  }

  async search(divisionId: string): Promise<SearchResult<WarehouseOutlineSegment>> {
    const items = await this.lockingIdbService.getAllFromIndex(divisionId, OUTLINES_STORE, 'division', divisionId);
    return { result: items as WarehouseOutlineSegment[], page: { size: items.length } };
  }

  async create(divisionId: string, segments: WarehouseOutlineSegment[]): Promise<void> {
    if (!segments.length) {
      return;
    }

    const segmentsWithIds: WarehouseOutlineSegment[] = generateAndAssignOutlineIds(segments);

    for (const segment of segmentsWithIds) {
      await this.dtoSigner.sign(segment, true);
      await this.idbService.add(OUTLINES_STORE, { ...segment, divisionId, division: { _id: divisionId } });

      const changeTyped: ChangeTyped<WarehouseOutlineSegment> = { ...segment, changeType: 'added' };
      this.warehouseSyncEventService.publishOutput({ division: divisionId, changes: [changeTyped], store: OUTLINES_STORE });
      super.publishEvent(ServiceEventType.CREATED, segment);
    }
  }

  async delete(divisionId: string, segments: WarehouseOutlineSegment[]): Promise<void> {
    if (!segments.length) {
      return;
    }

    const changes: ChangeTyped<WarehouseOutlineSegment>[] = [];
    for (const segment of segments) {
      await this.idbService.delete(OUTLINES_STORE, [divisionId, segment._id]);

      changes.push({ ...segment, changeType: 'removed' });
    }

    logger.info('Outlines deleted.', { count: segments.length, category: 'idb' });
    super.publishEvent(ServiceEventType.DELETED, ...segments);
    this.warehouseSyncEventService.publishOutput({ division: divisionId, changes, store: OUTLINES_STORE });
  }

  async update(divisionId: string, segments: WarehouseOutlineSegment[]): Promise<void> {
    if (!segments.length) {
      return;
    }

    for (const segment of segments) {
      await this.dtoSigner.sign(segment, false, false);
      await this.idbService.update(OUTLINES_STORE, { ...segment, divisionId });

      logger.debug('WarehouseOutlineService - update - publishOutput', { segmentId: segment._id, category: 'idb' });
      this.warehouseSyncEventService.publishOutput({
        division: divisionId,
        changes: [{ ...segment, changeType: 'modified' }],
        store: OUTLINES_STORE,
      });
    }
  }
}
